import { Operator } from "@/shared/model/Operator";
import "@/styles/SimpleKeyboardOverrides.css";
import { ErrorModal } from "@dexteel/mesf-core";
import {
  Box,
  Button,
  Dialog,
  InputAdornment,
  List,
  ListItem,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowDropDown as ArrowDropDownIcon } from "@mui/icons-material";
import { ArrowDropUp as ArrowDropUpIcon } from "@mui/icons-material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useState } from "react";
import { useSYOperators } from "@/pages/sy/hooks/useSYOperators";
import { CustomKeyboard } from "@/pages/sy/components/CustomKeyboard";

interface OperatorSelectorProps {
  selectedOperatorId: number | null;
  onChange: (operatorId: number) => void;
  operators?: Operator[];
  open?: boolean;
  onClose?: () => void;
}

export const OperatorSelector = ({
  selectedOperatorId,
  onChange,
  operators: propsOperators,
  open: externalOpen,
  onClose: externalOnClose,
}: OperatorSelectorProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isDialogOpen = externalOpen !== undefined ? externalOpen : internalOpen;

  const setIsDialogOpen =
    externalOnClose !== undefined
      ? (open: boolean) => {
          if (!open) externalOnClose();
        }
      : setInternalOpen;
  const [searchText, setSearchText] = useState("");
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [layoutName, setLayoutName] = useState<"default" | "shift" | "numbers">(
    "default",
  );

  const { syOperators, error, setError } = useSYOperators();

  const operatorsToUse = propsOperators || syOperators;

  const selectedOperator = operatorsToUse.find(
    (o) => o.OperatorId === selectedOperatorId,
  );

  const handleSelectOperator = (operatorId: number) => {
    onChange(operatorId);
    setIsDialogOpen(false);
    setKeyboardVisible(false);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setKeyboardVisible(false);
    setSearchText("");
  };

  const handleKeyboardChange = (input: string) => {
    setSearchText(input);
  };

  const handleKeyboardKeyPress = (button: string) => {
    if (button === "{shift}" || button === "{lock}") {
      setLayoutName((prev) =>
        prev === "default" ? "shift" : prev === "shift" ? "default" : prev,
      );
    } else if (button === "{numbers}") {
      setLayoutName((prev) => (prev === "numbers" ? "default" : "numbers"));
    } else if (button === "{abc}") {
      setLayoutName("default");
    }
  };

  const filteredOperators = operatorsToUse.filter((operator) =>
    operator.OperatorName.toLowerCase().includes(searchText.toLowerCase()),
  );

  return (
    <>
      {externalOnClose === undefined ? (
        <Box
          onClick={() => setIsDialogOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 1.5,
            bgcolor: "primary.main",
            color: "white",
            borderRadius: 1,
            cursor: "pointer",
            height: "48px",
            width: "100%",
            border: "none",
            boxShadow: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "1.5rem",
              textAlign: "center",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
            }}
          >
            {selectedOperator
              ? selectedOperator.OperatorName
              : "Select Operator"}
          </Typography>
        </Box>
      ) : null}

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="xl"
        slotProps={{
          paper: {
            sx: {
              minWidth: "500px",
              maxWidth: "90vw",
              borderRadius: "12px",
            },
          },
        }}
      >
        <Box
          sx={{
            p: 2,
            borderBottom: (theme) =>
              `1px solid ${theme.palette.charges.borders.medium}`,
            bgcolor: "primary.main",
            color: "white",
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, fontSize: "1.6rem" }}>
            Select Operator
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            borderBottom: (theme) =>
              `1px solid ${theme.palette.charges.borders.medium}`,
          }}
        >
          <TextField
            variant="outlined"
            fullWidth
            placeholder="Search operators..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  fontSize: "1.4rem",
                  "& .MuiInputBase-input": {
                    padding: "12px 14px",
                    height: "24px",
                  },
                  borderRadius: "8px",
                },
              },
            }}
          />

          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="text"
              color="primary"
              startIcon={
                keyboardVisible ? <ArrowDropDownIcon /> : <ArrowDropUpIcon />
              }
              onClick={() => setKeyboardVisible(!keyboardVisible)}
              sx={{ fontSize: "1rem" }}
            >
              {keyboardVisible ? "Hide Keyboard" : "Show Keyboard"}
            </Button>
          </Box>

          <Box sx={{ mt: 2, display: keyboardVisible ? "block" : "none" }}>
            <CustomKeyboard
              layoutName={layoutName}
              value={searchText}
              onChange={(input: string) => handleKeyboardChange(input)}
              onKeyPress={handleKeyboardKeyPress}
            />
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            maxHeight: "60vh",
            overflowY: "auto",
          }}
        >
          <List>
            {filteredOperators.map((operator) => (
              <ListItem
                key={operator.OperatorId}
                component="button"
                onClick={() =>
                  handleSelectOperator(operator.OperatorId as number)
                }
                sx={{
                  p: 3,
                  m: "4px 0",
                  border: (theme) =>
                    `1px solid ${theme.palette.charges.borders.medium}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  minHeight: "80px",
                  ...(operator.OperatorId === selectedOperatorId && {
                    borderLeft: "6px solid",
                    borderLeftColor: "primary.main",
                    bgcolor: (theme) =>
                      `rgba(${theme.palette.primary.main}, 0.08)`,
                  }),
                }}
              >
                <Typography sx={{ fontSize: "1.6rem" }}>
                  {operator.OperatorName}
                </Typography>
              </ListItem>
            ))}
          </List>
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: (theme) =>
              `1px solid ${theme.palette.charges.borders.medium}`,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="contained"
            color="inherit"
            onClick={handleCloseDialog}
            sx={{
              p: "12px 24px",
              fontSize: "1.4rem",
              fontWeight: 500,
              minWidth: "120px",
            }}
          >
            Cancel
          </Button>
        </Box>
      </Dialog>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
